import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import SmartDateInput from '../components/SmartDateInput'
import {
  Box, Text, Tabs, Group, Select, Button, Table, Badge,
  Center, Loader, ScrollArea, TextInput, Stack, Card, ActionIcon, Divider,
} from '@mantine/core'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import {
  IconPackage, IconArrowsExchange, IconTrendingDown,
  IconAdjustments, IconSearch, IconDownload, IconChevronDown, IconChevronUp,
} from '@tabler/icons-react'
import {
  getInventoryItems,
  getStockBalance, getStockMovementReport, getConsumptionReport, getAdjustmentReport,
  getStockCard,
} from '../api/client'

const KIND_COLOR: Record<string, string> = {
  RECEIVE: 'teal', ISSUE_DIRECT: 'orange', ISSUE_REPAIR: 'red',
  ADJUST: 'gray', RETURN: 'grape', TRANSFER: 'blue',
}
const thStyle = { fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' as const, letterSpacing: '0.3px' }

// ── การ์ดรายงานสำหรับมือถือ — สำเนาเดียวกับ ReportCard ใน ReportsPage.tsx (คนละไฟล์ ดึง import
// ข้ามหน้ากันไม่ได้ตรงๆ) โชว์ฟิลด์หลักเสมอ ที่เหลือพับไว้ กดที่การ์ดเพื่อขยายดูครบทุกคอลัมน์ ──
function ReportCard({ title, subtitle, badge, details }: {
  title: ReactNode
  subtitle?: ReactNode
  badge?: ReactNode
  details: { label: string; value: ReactNode }[]
}) {
  const [expanded, setExpanded] = useState(false)
  return (
    <Card withBorder radius="lg" p="sm" mb={8} onClick={() => setExpanded(e => !e)} style={{ cursor: 'pointer' }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Text size="sm" fw={600} lineClamp={1}>{title}</Text>
          {subtitle && <Text size="xs" c="dimmed" lineClamp={1} mt={1}>{subtitle}</Text>}
        </Box>
        <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
          {badge}
          <ActionIcon variant="subtle" color="gray" size="sm" style={{ pointerEvents: 'none' }}>
            {expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
          </ActionIcon>
        </Group>
      </Group>
      {expanded && details.length > 0 && (
        <>
          <Divider my={8} />
          <Stack gap={5}>
            {details.map((d, i) => (
              <Group key={i} gap={6} wrap="nowrap" align="flex-start">
                <Text size="xs" c="dimmed" style={{ minWidth: 92, flexShrink: 0 }}>{d.label}</Text>
                <Box style={{ fontSize: 12, minWidth: 0 }}>{d.value}</Box>
              </Group>
            ))}
          </Stack>
        </>
      )}
    </Card>
  )
}

function downloadCSV(filename: string, rows: any[], columns: { key: string; label: string }[]) {
  const header = columns.map(c => c.label).join(',')
  const body = rows.map(r => columns.map(c => {
    const v = String(r[c.key] ?? '').replace(/"/g, '""')
    return v.includes(',') || v.includes('"') ? `"${v}"` : v
  }).join(',')).join('\n')
  const blob = new Blob(['\ufeff' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

function DateRangeFilter({ dateFrom, dateTo, onChange }: {
  dateFrom: string; dateTo: string
  onChange: (f: string, t: string) => void
}) {
  return (
    <Group gap="xs">
      <SmartDateInput placeholder="DD/MM/YYYY" size="xs" value={dateFrom}
        onChange={v => onChange(v, dateTo)} />
      <Text size="xs" c="dimmed">—</Text>
      <SmartDateInput placeholder="DD/MM/YYYY" size="xs" value={dateTo}
        onChange={v => onChange(dateFrom, v)} />
    </Group>
  )
}

export default function StockReportsPage() {
  const { t } = useTranslation()
  const { isSmall, isMobile } = useBreakpoint()
  const [tab, setTab] = useState<string | null>('balance')
  const [loading, setLoading] = useState(false)

  const today = new Date().toISOString().slice(0, 10)
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)
  const [dateFrom, setDateFrom] = useState(monthAgo)
  const [dateTo, setDateTo] = useState(today)

  const [balanceData, setBalanceData]       = useState<any[] | null>(null)
  const [movementData, setMovementData]     = useState<any[] | null>(null)
  const [movKindFilter, setMovKindFilter]   = useState<string | null>(null)
  const [consumptionData, setConsumptionData] = useState<any[] | null>(null)
  const [adjustmentData, setAdjustmentData] = useState<any[] | null>(null)
  const [items, setItems]             = useState<any[]>([])
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [cardData, setCardData]       = useState<any | null>(null)
  const [itemsLoaded, setItemsLoaded] = useState(false)

  const tr = (k: string) => t(`stockReport.${k}`)

  const kindLabel = (k: string) => {
    const map: Record<string, string> = {
      RECEIVE: tr('kindReceive'), ISSUE_DIRECT: tr('kindIssueDirect'),
      ISSUE_REPAIR: tr('kindIssueRepair'), ADJUST: tr('kindAdjust'),
      RETURN: tr('kindReturn'), TRANSFER: tr('kindTransfer'),
    }
    return map[k] ?? k
  }

  const loadItems = async () => {
    if (itemsLoaded) return
    const { data } = await getInventoryItems()
    setItems(data ?? [])
    setItemsLoaded(true)
  }

  const run = async () => {
    setLoading(true)
    try {
      if (tab === 'balance') {
        const { data } = await getStockBalance(dateFrom || undefined, dateTo || undefined)
        setBalanceData(data)
      } else if (tab === 'movement') {
        const { data } = await getStockMovementReport(dateFrom || undefined, dateTo || undefined, movKindFilter || undefined)
        setMovementData(data)
      } else if (tab === 'consumption') {
        const { data } = await getConsumptionReport(dateFrom || undefined, dateTo || undefined)
        setConsumptionData(data)
      } else if (tab === 'adjustment') {
        const { data } = await getAdjustmentReport(dateFrom || undefined, dateTo || undefined)
        setAdjustmentData(data)
      } else if (tab === 'stockcard') {
        if (!selectedItemId) { notifications.show({ color: 'red', message: tr('selectItemFirst') }); return }
        const { data } = await getStockCard(+selectedItemId, dateFrom || undefined, dateTo || undefined)
        setCardData(data)
      }
    } catch { notifications.show({ color: 'red', message: t('common.error') }) }
    finally { setLoading(false) }
  }

  const fmtDate = (d: string) => new Date(d).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })
  const fmtMoney = (n: number) => `฿${Number(n).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`
  const emptyRow = (cols: number, msg: string) => (
    <Table.Tr><Table.Td colSpan={cols}><Center p="xl"><Text size="sm" c="dimmed">{msg}</Text></Center></Table.Td></Table.Tr>
  )

  const kindSelectData = [
    { value: 'RECEIVE', label: tr('kindReceive') },
    { value: 'ISSUE_DIRECT', label: tr('kindIssueDirect') },
    { value: 'ISSUE_REPAIR', label: tr('kindIssueRepair') },
    { value: 'ADJUST', label: tr('kindAdjust') },
    { value: 'RETURN', label: tr('kindReturn') },
    { value: 'TRANSFER', label: tr('kindTransfer') },
  ]

  return (
    <Box p={isSmall ? 16 : 32}>
      <Box mb="lg">
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 18 : 22, fontWeight: 600, letterSpacing: '-0.3px', color: '#0f172a' }}>
          {t('stock.reportsTitle', 'Stock Reports')}
        </Text>
        <Text size="sm" c="dimmed">{t('stock.reportsSubtitle')}</Text>
      </Box>

      <Tabs value={tab} onChange={v => { setTab(v); if (v === 'stockcard') loadItems() }} mb="md">
        <Tabs.List>
          <Tabs.Tab value="balance"     leftSection={<IconPackage size={13} />}>{tr('balance')}</Tabs.Tab>
          <Tabs.Tab value="movement"    leftSection={<IconArrowsExchange size={13} />}>{tr('movement')}</Tabs.Tab>
          <Tabs.Tab value="consumption" leftSection={<IconTrendingDown size={13} />}>{tr('consumption')}</Tabs.Tab>
          <Tabs.Tab value="adjustment"  leftSection={<IconAdjustments size={13} />}>{tr('adjustment')}</Tabs.Tab>
          <Tabs.Tab value="stockcard"   leftSection={<IconPackage size={13} />}>{tr('stockCard')}</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {/* ── Filter Bar ── */}
      <Group gap="sm" mb="md" wrap="wrap">
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo}
          onChange={(f, t2) => { setDateFrom(f); setDateTo(t2) }} />
        {tab === 'movement' && (
          <Select size="xs" clearable placeholder={tr('allKinds')}
            data={kindSelectData} value={movKindFilter} onChange={setMovKindFilter} style={{ width: isMobile ? '100%' : 150 }} />
        )}
        {tab === 'stockcard' && (
          <Select size="xs" searchable placeholder={tr('selectItem')}
            data={items.map(i => ({ value: String(i.id), label: `${i.sku} — ${i.name}` }))}
            value={selectedItemId} onChange={setSelectedItemId} style={{ width: isMobile ? '100%' : 260 }} />
        )}
        <Button size="xs" leftSection={<IconSearch size={14} />} onClick={run}
          style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)' }}>
          {t('common.search', 'ค้นหา')}
        </Button>
        {loading && <Loader size="xs" />}
      </Group>

      {/* ── STOCK BALANCE ── */}
      {tab === 'balance' && (
        <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
          {balanceData && (
            <Group justify="flex-end" p="sm">
              <Button size="xs" variant="subtle" leftSection={<IconDownload size={14} />}
                onClick={() => downloadCSV('stock-balance.csv', balanceData, [
                  { key: 'sku', label: tr('colSKU') }, { key: 'name', label: tr('colProduct') }, { key: 'category', label: tr('colCategory') },
                  { key: 'stockQty', label: tr('colBalance') }, { key: 'minStock', label: 'Min' },
                  { key: 'unit', label: tr('colUnit') }, { key: 'unitCost', label: tr('colUnitCost') }, { key: 'totalValue', label: tr('colTotalValue') },
                ])}>CSV</Button>
            </Group>
          )}
          {isMobile ? (
            !balanceData ? (
              <Center p="xl"><Text size="sm" c="dimmed">{tr('pressSearch')}</Text></Center>
            ) : balanceData.length === 0 ? (
              <Center p="xl"><Text size="sm" c="dimmed">{tr('noData')}</Text></Center>
            ) : (
              <Box p="xs">
                {balanceData.map((i: any) => (
                  <ReportCard key={i.id}
                    title={i.name}
                    subtitle={i.sku}
                    badge={<Badge size="xs" color={i.isOut ? 'red' : i.isLow ? 'orange' : 'teal'} variant="light">
                      {i.isOut ? tr('statusOut') : i.isLow ? tr('statusLow') : tr('statusNormal')}
                    </Badge>}
                    details={[
                      { label: tr('colCategory'), value: i.category ?? '—' },
                      { label: tr('colBalance'), value: <Text size="xs" fw={600} c={i.isOut ? 'red' : i.isLow ? 'orange' : 'teal'}>{i.stockQty}</Text> },
                      { label: 'Min', value: i.minStock },
                      { label: tr('colUnitCost'), value: fmtMoney(i.unitCost) },
                      { label: tr('colTotalValue'), value: fmtMoney(i.totalValue) },
                    ]}
                  />
                ))}
              </Box>
            )
          ) : (
          <ScrollArea>
            <Table highlightOnHover>
              <Table.Thead style={{ background: '#f8fafc' }}>
                <Table.Tr>{[tr('colSKU'),tr('colProduct'),tr('colCategory'),tr('colBalance'),'Min',tr('colUnit'),tr('colUnitCost'),tr('colTotalValue'),tr('colStatus')].map(h => <Table.Th key={h} style={thStyle}>{h}</Table.Th>)}</Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {!balanceData ? emptyRow(9, tr('pressSearch'))
                  : balanceData.map((i: any) => (
                  <Table.Tr key={i.id}>
                    <Table.Td><Text size="xs" c="dimmed">{i.sku}</Text></Table.Td>
                    <Table.Td><Text size="xs" fw={500}>{i.name}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{i.category ?? '—'}</Text></Table.Td>
                    <Table.Td><Text size="xs" fw={600} c={i.isOut ? 'red' : i.isLow ? 'orange' : 'teal'}>{i.stockQty}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{i.minStock}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{i.unit}</Text></Table.Td>
                    <Table.Td><Text size="xs">{fmtMoney(i.unitCost)}</Text></Table.Td>
                    <Table.Td><Text size="xs" fw={500}>{fmtMoney(i.totalValue)}</Text></Table.Td>
                    <Table.Td><Badge size="xs" color={i.isOut ? 'red' : i.isLow ? 'orange' : 'teal'} variant="light">
                      {i.isOut ? tr('statusOut') : i.isLow ? tr('statusLow') : tr('statusNormal')}
                    </Badge></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          )}
        </Box>
      )}

      {/* ── STOCK MOVEMENT ── */}
      {tab === 'movement' && (
        <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
          {movementData && (
            <Group justify="flex-end" p="sm">
              <Button size="xs" variant="subtle" leftSection={<IconDownload size={14} />}
                onClick={() => downloadCSV('stock-movement.csv', movementData.map(m => ({
                  ...m, itemName: m.item?.name, kind: kindLabel(m.kind),
                  location: m.location?.name ?? (m.fromLocation && m.toLocation ? `${m.fromLocation.name} → ${m.toLocation.name}` : '—'),
                  date: new Date(m.createdAt).toLocaleString('th-TH'),
                })), [
                  { key: 'date', label: tr('colDate') }, { key: 'itemName', label: tr('colProduct') }, { key: 'kind', label: tr('colType') },
                  { key: 'qty', label: tr('colQty') }, { key: 'balanceAfter', label: tr('colAfter') }, { key: 'location', label: tr('colLocation') },
                  { key: 'performedBy', label: tr('colBy') }, { key: 'note', label: tr('colNote') },
                ])}>CSV</Button>
            </Group>
          )}
          {isMobile ? (
            !movementData ? (
              <Center p="xl"><Text size="sm" c="dimmed">{tr('pressSearch')}</Text></Center>
            ) : movementData.length === 0 ? (
              <Center p="xl"><Text size="sm" c="dimmed">{tr('noData')}</Text></Center>
            ) : (
              <Box p="xs">
                {movementData.map((m: any) => (
                  <ReportCard key={m.id}
                    title={m.item?.name ?? '—'}
                    subtitle={fmtDate(m.createdAt)}
                    badge={<Badge size="xs" color={KIND_COLOR[m.kind] ?? 'gray'} variant="light">{kindLabel(m.kind)}</Badge>}
                    details={[
                      { label: tr('colQty'), value: <Text size="xs" fw={600} c={m.qty > 0 ? 'teal' : 'red'}>{m.qty > 0 ? `+${m.qty}` : m.qty}</Text> },
                      { label: tr('colAfter'), value: m.balanceAfter },
                      { label: tr('colLocation'), value: m.kind === 'TRANSFER' ? `${m.fromLocation?.name ?? '?'}→${m.toLocation?.name ?? '?'}` : m.location?.name ?? '—' },
                      { label: tr('colBy'), value: m.performedBy },
                      { label: tr('colNote'), value: m.note ?? '—' },
                    ]}
                  />
                ))}
              </Box>
            )
          ) : (
          <ScrollArea>
            <Table highlightOnHover>
              <Table.Thead style={{ background: '#f8fafc' }}>
                <Table.Tr>{[tr('colDate'),tr('colProduct'),tr('colType'),tr('colQty'),tr('colAfter'),tr('colLocation'),tr('colBy'),tr('colNote')].map(h => <Table.Th key={h} style={thStyle}>{h}</Table.Th>)}</Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {!movementData ? emptyRow(8, tr('pressSearch'))
                  : movementData.length === 0 ? emptyRow(8, tr('noData'))
                  : movementData.map((m: any) => (
                  <Table.Tr key={m.id}>
                    <Table.Td><Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{fmtDate(m.createdAt)}</Text></Table.Td>
                    <Table.Td><Text size="xs" fw={500} lineClamp={1}>{m.item?.name ?? '—'}</Text></Table.Td>
                    <Table.Td><Badge size="xs" color={KIND_COLOR[m.kind] ?? 'gray'} variant="light">{kindLabel(m.kind)}</Badge></Table.Td>
                    <Table.Td><Text size="xs" fw={600} c={m.qty > 0 ? 'teal' : 'red'}>{m.qty > 0 ? `+${m.qty}` : m.qty}</Text></Table.Td>
                    <Table.Td><Text size="xs">{m.balanceAfter}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{m.kind === 'TRANSFER' ? `${m.fromLocation?.name ?? '?'}→${m.toLocation?.name ?? '?'}` : m.location?.name ?? '—'}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{m.performedBy}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed" lineClamp={1} style={{ maxWidth: 160 }}>{m.note ?? '—'}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          )}
        </Box>
      )}

      {/* ── CONSUMPTION REPORT ── */}
      {tab === 'consumption' && (
        <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
          {consumptionData && (
            <Group justify="flex-end" p="sm">
              <Button size="xs" variant="subtle" leftSection={<IconDownload size={14} />}
                onClick={() => downloadCSV('consumption-report.csv', consumptionData, [
                  { key: 'sku', label: tr('colSKU') }, { key: 'name', label: tr('colProduct') }, { key: 'category', label: tr('colCategory') },
                  { key: 'totalUsed', label: tr('colUsed') }, { key: 'unit', label: tr('colUnit') }, { key: 'totalCost', label: tr('colCost') }, { key: 'txCount', label: tr('colTimes') },
                ])}>CSV</Button>
            </Group>
          )}
          {isMobile ? (
            !consumptionData ? (
              <Center p="xl"><Text size="sm" c="dimmed">{tr('pressSearch')}</Text></Center>
            ) : consumptionData.length === 0 ? (
              <Center p="xl"><Text size="sm" c="dimmed">{tr('noData')}</Text></Center>
            ) : (
              <Box p="xs">
                {consumptionData.map((r: any) => (
                  <ReportCard key={r.itemId}
                    title={r.name}
                    subtitle={r.sku}
                    badge={<Text size="xs" fw={600} c="orange">{r.totalUsed.toLocaleString()} {r.unit}</Text>}
                    details={[
                      { label: tr('colCategory'), value: r.category ?? '—' },
                      { label: tr('colCost'), value: fmtMoney(r.totalCost) },
                      { label: tr('colTimes'), value: r.txCount },
                    ]}
                  />
                ))}
              </Box>
            )
          ) : (
          <ScrollArea>
            <Table highlightOnHover>
              <Table.Thead style={{ background: '#f8fafc' }}>
                <Table.Tr>{[tr('colSKU'),tr('colProduct'),tr('colCategory'),tr('colUsed'),tr('colUnit'),tr('colCost'),tr('colTimes')].map(h => <Table.Th key={h} style={thStyle}>{h}</Table.Th>)}</Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {!consumptionData ? emptyRow(7, tr('pressSearch'))
                  : consumptionData.length === 0 ? emptyRow(7, tr('noData'))
                  : consumptionData.map((r: any) => (
                  <Table.Tr key={r.itemId}>
                    <Table.Td><Text size="xs" c="dimmed">{r.sku}</Text></Table.Td>
                    <Table.Td><Text size="xs" fw={500}>{r.name}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{r.category ?? '—'}</Text></Table.Td>
                    <Table.Td><Text size="xs" fw={600} c="orange">{r.totalUsed.toLocaleString()}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{r.unit}</Text></Table.Td>
                    <Table.Td><Text size="xs" fw={500}>{fmtMoney(r.totalCost)}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{r.txCount}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          )}
        </Box>
      )}

      {/* ── ADJUSTMENT REPORT ── */}
      {tab === 'adjustment' && (
        <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
          {adjustmentData && (
            <Group justify="flex-end" p="sm">
              <Button size="xs" variant="subtle" leftSection={<IconDownload size={14} />}
                onClick={() => downloadCSV('adjustment-report.csv', adjustmentData.map(m => ({
                  date: new Date(m.createdAt).toLocaleString('th-TH'),
                  name: m.item?.name, sku: m.item?.sku, qty: m.qty, balanceAfter: m.balanceAfter,
                  location: m.location?.name ?? '—', performedBy: m.performedBy, note: m.note,
                })), [
                  { key: 'date', label: tr('colDate') }, { key: 'name', label: tr('colProduct') }, { key: 'sku', label: tr('colSKU') },
                  { key: 'qty', label: tr('colDiff') }, { key: 'balanceAfter', label: tr('colAfter') }, { key: 'location', label: tr('colLocation') },
                  { key: 'performedBy', label: tr('colBy') }, { key: 'note', label: tr('colReason') },
                ])}>CSV</Button>
            </Group>
          )}
          {isMobile ? (
            !adjustmentData ? (
              <Center p="xl"><Text size="sm" c="dimmed">{tr('pressSearch')}</Text></Center>
            ) : adjustmentData.length === 0 ? (
              <Center p="xl"><Text size="sm" c="dimmed">{tr('noData')}</Text></Center>
            ) : (
              <Box p="xs">
                {adjustmentData.map((m: any) => (
                  <ReportCard key={m.id}
                    title={m.item?.name}
                    subtitle={fmtDate(m.createdAt)}
                    badge={<Text size="xs" fw={600} c={m.qty > 0 ? 'teal' : 'red'}>{m.qty > 0 ? `+${m.qty}` : m.qty}</Text>}
                    details={[
                      { label: tr('colSKU'), value: m.item?.sku },
                      { label: tr('colAfter'), value: m.balanceAfter },
                      { label: tr('colLocation'), value: m.location?.name ?? '—' },
                      { label: tr('colBy'), value: m.performedBy },
                      { label: tr('colReason'), value: m.note ?? '—' },
                    ]}
                  />
                ))}
              </Box>
            )
          ) : (
          <ScrollArea>
            <Table highlightOnHover>
              <Table.Thead style={{ background: '#f8fafc' }}>
                <Table.Tr>{[tr('colDate'),tr('colProduct'),tr('colSKU'),tr('colDiff'),tr('colAfter'),tr('colLocation'),tr('colBy'),tr('colReason')].map(h => <Table.Th key={h} style={thStyle}>{h}</Table.Th>)}</Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {!adjustmentData ? emptyRow(8, tr('pressSearch'))
                  : adjustmentData.length === 0 ? emptyRow(8, tr('noData'))
                  : adjustmentData.map((m: any) => (
                  <Table.Tr key={m.id}>
                    <Table.Td><Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{fmtDate(m.createdAt)}</Text></Table.Td>
                    <Table.Td><Text size="xs" fw={500}>{m.item?.name}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{m.item?.sku}</Text></Table.Td>
                    <Table.Td><Text size="xs" fw={600} c={m.qty > 0 ? 'teal' : 'red'}>{m.qty > 0 ? `+${m.qty}` : m.qty}</Text></Table.Td>
                    <Table.Td><Text size="xs">{m.balanceAfter}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{m.location?.name ?? '—'}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{m.performedBy}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed" lineClamp={1} style={{ maxWidth: 180 }}>{m.note ?? '—'}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          )}
        </Box>
      )}

      {/* ── STOCK CARD ── */}
      {tab === 'stockcard' && (
        <Box>
          {cardData && (
            <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
              <Box p="md" style={{ borderBottom: '1px solid #f1f5f9' }}>
                <Text fw={600}>{cardData.item?.name} ({cardData.item?.sku})</Text>
                <Text size="xs" c="dimmed">{tr('colUnit')}: {cardData.item?.unit} | {tr('colBalance')}: <b>{cardData.item?.stockQty}</b></Text>
              </Box>
              <Group justify="flex-end" p="sm">
                <Button size="xs" variant="subtle" leftSection={<IconDownload size={14} />}
                  onClick={() => downloadCSV(`stock-card-${cardData.item?.sku}.csv`, cardData.movements.map((m: any) => ({
                    date: new Date(m.createdAt).toLocaleString('th-TH'),
                    kind: kindLabel(m.kind),
                    qtyIn: m.qty > 0 ? m.qty : '',
                    qtyOut: m.qty < 0 ? Math.abs(m.qty) : '',
                    balanceAfter: m.balanceAfter,
                    location: m.location?.name ?? '—',
                    performedBy: m.performedBy, note: m.note,
                  })), [
                    { key: 'date', label: tr('colDate') }, { key: 'kind', label: tr('colType') },
                    { key: 'qtyIn', label: tr('colQtyIn') }, { key: 'qtyOut', label: tr('colQtyOut') },
                    { key: 'balanceAfter', label: tr('colBalance') }, { key: 'location', label: tr('colLocation') },
                    { key: 'performedBy', label: tr('colBy') }, { key: 'note', label: tr('colNote') },
                  ])}>CSV</Button>
              </Group>
              {isMobile ? (
                cardData.movements.length === 0 ? (
                  <Center p="xl"><Text size="sm" c="dimmed">{tr('noPeriodData')}</Text></Center>
                ) : (
                  <Box p="xs">
                    {cardData.movements.map((m: any) => (
                      <ReportCard key={m.id}
                        title={kindLabel(m.kind)}
                        subtitle={fmtDate(m.createdAt)}
                        badge={<Text size="xs" fw={700}>{tr('colBalance')}: {m.balanceAfter}</Text>}
                        details={[
                          { label: tr('colQtyIn'), value: <Text size="xs" fw={600} c="teal">{m.qty > 0 ? m.qty : '—'}</Text> },
                          { label: tr('colQtyOut'), value: <Text size="xs" fw={600} c="orange">{m.qty < 0 ? Math.abs(m.qty) : '—'}</Text> },
                          { label: tr('colLocation'), value: m.kind === 'TRANSFER' ? `${m.fromLocation?.name ?? '?'}→${m.toLocation?.name ?? '?'}` : m.location?.name ?? '—' },
                          { label: tr('colBy'), value: m.performedBy },
                          { label: tr('colNote'), value: m.note ?? '—' },
                        ]}
                      />
                    ))}
                  </Box>
                )
              ) : (
              <ScrollArea>
                <Table highlightOnHover>
                  <Table.Thead style={{ background: '#f8fafc' }}>
                    <Table.Tr>{[tr('colDate'),tr('colType'),tr('colQtyIn'),tr('colQtyOut'),tr('colBalance'),tr('colLocation'),tr('colBy'),tr('colNote')].map(h => <Table.Th key={h} style={thStyle}>{h}</Table.Th>)}</Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {cardData.movements.length === 0 ? emptyRow(8, tr('noPeriodData'))
                      : cardData.movements.map((m: any) => (
                      <Table.Tr key={m.id}>
                        <Table.Td><Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{fmtDate(m.createdAt)}</Text></Table.Td>
                        <Table.Td><Badge size="xs" color={KIND_COLOR[m.kind] ?? 'gray'} variant="light">{kindLabel(m.kind)}</Badge></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="teal">{m.qty > 0 ? m.qty : '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="orange">{m.qty < 0 ? Math.abs(m.qty) : '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={700}>{m.balanceAfter}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{m.kind === 'TRANSFER' ? `${m.fromLocation?.name ?? '?'}→${m.toLocation?.name ?? '?'}` : m.location?.name ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{m.performedBy}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed" lineClamp={1} style={{ maxWidth: 160 }}>{m.note ?? '—'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
              )}
            </Box>
          )}
          {!cardData && (
            <Center py={60}>
              <Stack align="center" gap="xs">
                <IconPackage size={40} color="#cbd5e1" />
                <Text size="sm" c="dimmed">{tr('selectItemThenSearch')}</Text>
              </Stack>
            </Center>
          )}
        </Box>
      )}
    </Box>
  )
}
